<template>
    <div>
        <div class="w-full pt-2 px-2" v-if="errorHandler.hasErrorMessage()">
            <p class="error pause-error--message" v-text="errorHandler.getErrorMessage()"></p>
        </div>
        <div class="pause--container">
            <pause-reason v-if="stage === 'pause-reason'" :subscriptionId="subscriptionId" v-model="pauseSurveyAnswer" @deliveries-paused="handleDeliveriesPaused"/>

            <component v-else-if="stage !== 'pause-reason'"
                :is="nextStageComponent"
                :subscriptionId="subscriptionId"
                :pauseSurveyAnswer="pauseSurveyAnswer"
                :plans="plans"
                :availableRestartDates="availableRestartDates"
                :couponDetails="couponDetails"
                v-model="nextSteps"
                @deliveries-paused="handleDeliveriesPaused"
                @subscription-updated="handleSubscriptionUpdated" />
            <a
                v-if="!getPauseIsCancel"
                @click.prevent="closePauseDeliveries"
                class="btn btn--secondary btn--full-width cancel-pause--button">
                {{ $t('PAUSE_DELIVERIES_RESUME') }}
            </a>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CmsService from '@/services/cms/CmsService';
import PostcodeService from '@/services/logistics/PostcodeService';
import CouponService from '@/services/coupons/CouponService';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import PauseQuestion from '@/components/partials/account/my-delivery-addresses/PauseQuestion';
import PauseReason from '@/components/partials/account/my-delivery-addresses/PauseReason';
import { ConfirmationStep, DiscountStep, LilyFreeStep, ReduceFrequencyStep, RestartStep, WeeklyEmailStep, FlowersStep } from './pauseSteps';

let cmsService = new CmsService();

export default {
    name: 'PauseDeliveries',
    components: { PauseQuestion, PauseReason, ConfirmationStep, DiscountStep, LilyFreeStep, ReduceFrequencyStep, RestartStep, WeeklyEmailStep, FlowersStep },
    props: {
        subscriptionId: {
            required: true,
            type: Number,
        },
        isSubscriptionPaused: {
            required: true,
            type: Boolean,
        },
        addressId: {
            required: true,
            type: Number,
        },
    },
    data() {
        return {
            stage: 'pause-reason',
            pauseSurveyAnswerId: null,
            pauseSurveyAnswer: null,
            nextSteps: null,
            showPausedDeliveriesConfirmationModal: false,
            errorHandler: new ErrorResponseHandler(),
            plans: [],
            availableRestartDates: [],
            couponDetails: null,
        };
    },
    mounted() {
        this.fetchPlans();
        this.fetchRestartDatesForPostcode(this.addressDetails.postcode);
        this.fetchCurrentPauseCoupon();
    },
    computed: {
        ...mapGetters({
            user: 'account/getUser',
            getPauseIsCancel: 'subscription/getPauseIsCancel',
            isUSA: 'localisation/isUSA',
        }),
        nextStageComponent() {
            return `${this.stage}-step`;
        },
        subscriptionDetails() {
            return this.$store.getters['subscription/getSubscriptionById'](this.subscriptionId);
        },
        addressDetails() {
            return this.$store.getters['address/getAddressById'](this.addressId);
        },
        currentPlanFrequency() {
            return this.subscriptionDetails?.plan_slug;
        },
    },
    watch: {
        pauseSurveyAnswer: {
            handler() {
                this.pauseSurveyAnswerId = this.pauseSurveyAnswer.id;
                const firstStep = this.pauseSurveyAnswer?.nextSteps[0];
                const secondStep = this.pauseSurveyAnswer?.nextSteps[1];
                const skipFirstStep = this.skipStep(firstStep);
                if (this.isUSA && this.pauseSurveyAnswerId === 1 && this.currentPlanFrequency === 'monthly') {
                    this.stage = this.pauseSurveyAnswer?.nextSteps[2];
                } else if (skipFirstStep) {
                    this.stage = this.skipStep(secondStep) ? 'confirmation' : this.pauseSurveyAnswer?.nextSteps[1];
                } else {
                    this.stage = firstStep;
                }
            },
        },
        nextSteps: {
            handler() {
                if (this.nextSteps && this.nextSteps[0] !== null) {
                    const nextStep = this.skipStep(this.nextSteps[0]) ? 'confirmation' : this.nextSteps[0];
                    this.stage = nextStep;
                }
            },
        },
    },
    methods: {
        fetchData() {
            this.$store.dispatch('subscription/fetchAllSubscriptions', true);
            this.$store.dispatch('address/fetchAllAddresses', true);
            this.$store.dispatch('scheduledOrder/fetchAllDeliveries', true);
        },
        fetchPlans() {
            cmsService.getPlans().then(response => {
                const planFrequencies = response.data.data.map(item => {
                    return item.slug;
                });
                this.plans = planFrequencies;
            }).catch(error => console.error(error));
        },

        fetchRestartDatesForPostcode(postcode) {
            PostcodeService.getDeliveryDatesForPostcode(postcode, 4)
                .then(response => {
                    this.availableRestartDates = response.data.items.map(item => {
                        return {
                            label: item.human,
                            value: item.value,
                        };
                    });
                })
                .catch(error => console.error(error));
        },

        fetchCurrentPauseCoupon() {
            CouponService.getCurrentPauseCoupon().then(response => {
                this.couponDetails = response.data;
            }).catch(error => {
                this.errorHandler.setResponse(error.response.data);
                this.$emit('input', this.updatedSteps);
            });
        },

        skipStep(step) {
            const userGetsWeeklyEmail = this.user.weekly_email;
            const userOnMonthlySubscription = this.currentPlanFrequency === 'monthly';
            const skipDiscountStep = (step === 'discount' && !this.user.pause_sub_coupon_redeemed) || (step === 'discount' && !this.couponDetails);
            const skipWeeklyEmailStep = step === 'weekly-email' && userGetsWeeklyEmail;
            const skipReduceFrequency = step === 'reduce-frequency' && userOnMonthlySubscription;
            const skipLilyFree = step === 'lily-free' && this.user.dislikes_lilies;
            return skipDiscountStep || skipWeeklyEmailStep || skipReduceFrequency || skipLilyFree;
        },

        closePauseDeliveries() {
            this.stage = 'pause-reason';
            this.nextSteps = null;
            this.$emit('closed');
        },

        handleDeliveriesPaused(whyLeave) {
            switch (whyLeave) {
                case 'lilyFree':
                    this.$emit('deliveries-paused', 'lilyFree');
                    break;
                case 'goingAway':
                    this.$emit('deliveries-paused', 'goingAway');
                    break;
                case 'renovatingHouse':
                    this.$emit('deliveries-paused', 'renovatingHouse');
                    break;
                default:
                    this.$emit('deliveries-paused');
            }
            this.fetchData();
            this.closePauseDeliveries();
        },

        handleSubscriptionUpdated() {
            this.$emit('subscription-updated');
            this.fetchData();
            this.closePauseDeliveries();
        },
    },
};
</script>

<style lang="scss">

.pause--container {
    padding: 15px 25px 20px;
    border: 1px solid black;
    background-color: white;
}

.pause--heading {
    font-size: 25px;
    line-height: 30px;
    text-align: left;
}

.cancel-pause--button {
    margin: 15px 0 15px;
    cursor: pointer;
    text-align: center;
}

.pause-error--message {
    margin: 0;
    padding: 0 16px;
    font-size: 16px;
}

</style>
